import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Paper,
  List,
  ListItem,
  ListItemText,
  Button,
  Divider,
  Alert,
  TextField,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import InstallMobileIcon from '@mui/icons-material/InstallMobile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { useAuth } from '../contexts/AuthContext';
import { getUserSettings, updateUserSettings, checkHandleAvailability } from '../services/emails';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;

// Listen globally so we capture the event even before Settings mounts
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  deferredPrompt = e as BeforeInstallPromptEvent;
});

export default function SettingsPage() {
  const { user, logout } = useAuth();
  const [installable, setInstallable] = useState(!!deferredPrompt);
  const [installed, setInstalled] = useState(
    window.matchMedia('(display-mode: standalone)').matches,
  );
  const [handle, setHandle] = useState('');
  const [handleStatus, setHandleStatus] = useState<'idle' | 'checking' | 'available' | 'taken' | 'invalid'>('idle');
  const [handleSaving, setHandleSaving] = useState(false);
  const [handleSaved, setHandleSaved] = useState(false);
  const [handleError, setHandleError] = useState<string | null>(null);
  const [handle2, setHandle2] = useState('');
  const [handle2Status, setHandle2Status] = useState<'idle' | 'checking' | 'available' | 'taken' | 'invalid'>('idle');
  const [handle2Saving, setHandle2Saving] = useState(false);
  const [handle2Saved, setHandle2Saved] = useState(false);
  const [handle2Error, setHandle2Error] = useState<string | null>(null);
  const checkRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const check2Ref = useRef<ReturnType<typeof setTimeout>>(undefined);
  const handleInputRef = useRef<HTMLInputElement>(null);
  const INBOUND_DOMAIN = 'stash.memespin.com';

  useEffect(() => {
    getUserSettings().then(s => {
      if (s.inboundHandle) setHandle(s.inboundHandle);
      if (s.inboundHandle2) setHandle2(s.inboundHandle2);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (window.location.hash === '#inbound-handle') {
      setTimeout(() => {
        handleInputRef.current?.focus();
        handleInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      deferredPrompt = e as BeforeInstallPromptEvent;
      setInstallable(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    const installedHandler = () => setInstalled(true);
    window.addEventListener('appinstalled', installedHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  const handleHandleChange = (value: string) => {
    const v = value.toLowerCase().replace(/[^a-z0-9._-]/g, '');
    setHandle(v);
    setHandleStatus('idle');
    setHandleSaved(false);
    setHandleError(null);
    clearTimeout(checkRef.current);
    if (!v) return;
    if (!/^[a-z0-9][a-z0-9._-]{1,30}[a-z0-9]$/.test(v)) {
      setHandleStatus('invalid');
      return;
    }
    setHandleStatus('checking');
    checkRef.current = setTimeout(async () => {
      try {
        const available = await checkHandleAvailability(v);
        setHandleStatus(available ? 'available' : 'taken');
      } catch {
        setHandleStatus('idle');
      }
    }, 600);
  };

  const handleSaveHandle = async () => {
    setHandleSaving(true);
    setHandleError(null);
    try {
      await updateUserSettings({ inboundHandle: handle || null });
      setHandleSaved(true);
      setHandleStatus('idle');
    } catch {
      setHandleError('Failed to save. Please try again.');
    } finally {
      setHandleSaving(false);
    }
  };

  const handleHandle2Change = (value: string) => {
    const v = value.toLowerCase().replace(/[^a-z0-9._-]/g, '');
    setHandle2(v);
    setHandle2Status('idle');
    setHandle2Saved(false);
    setHandle2Error(null);
    clearTimeout(check2Ref.current);
    if (!v) return;
    if (!/^[a-z0-9][a-z0-9._-]{1,30}[a-z0-9]$/.test(v)) {
      setHandle2Status('invalid');
      return;
    }
    setHandle2Status('checking');
    check2Ref.current = setTimeout(async () => {
      try {
        const available = await checkHandleAvailability(v);
        setHandle2Status(available ? 'available' : 'taken');
      } catch {
        setHandle2Status('idle');
      }
    }, 600);
  };

  const handleSaveHandle2 = async () => {
    setHandle2Saving(true);
    setHandle2Error(null);
    try {
      await updateUserSettings({ inboundHandle2: handle2 || null });
      setHandle2Saved(true);
      setHandle2Status('idle');
    } catch {
      setHandle2Error('Failed to save. Please try again.');
    } finally {
      setHandle2Saving(false);
    }
  };

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setInstalled(true);
    }
    deferredPrompt = null;
    setInstallable(false);
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 500 }}>
        Settings
      </Typography>

      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            src={user?.photoURL ?? ''}
            alt={user?.displayName ?? ''}
            sx={{ width: 56, height: 56 }}
          />
          <Box>
            <Typography variant="h6">{user?.displayName}</Typography>
            <Typography variant="body2" color="text.secondary">
              {user?.email}
            </Typography>
          </Box>
        </Box>
        <Divider />
        <List disablePadding>
          <ListItem>
            <ListItemText
              primary="Account"
              secondary={`Signed in with Google as ${user?.email}`}
            />
          </ListItem>
        </List>
      </Paper>

      {installed ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          Stash is installed as an app.
        </Alert>
      ) : installable ? (
        <Button
          variant="contained"
          startIcon={<InstallMobileIcon />}
          onClick={handleInstall}
          fullWidth
          sx={{ mb: 2 }}
        >
          Install Stash App
        </Button>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>
          Install not available — open in Chrome and ensure the service worker is
          active (reload the page once).
        </Alert>
      )}

      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Box sx={{ p: 3 }}>
          <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 1 }}>
            Inbound Email Address
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Send or forward emails to this address to save them as Stash items.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              size="small"
              value={handle}
              onChange={e => handleHandleChange(e.target.value)}
              placeholder="yourhandle"
              inputRef={handleInputRef}
              helperText={
                handleError ? handleError :
                handleStatus === 'invalid' ? 'Use 3–32 lowercase letters, numbers, dots, hyphens' :
                handleStatus === 'taken' ? 'This mailbox is not available' :
                handleStatus === 'available' ? `Your address: ${handle}@${INBOUND_DOMAIN}` :
                handleSaved ? `Saved! Email to ${handle}@${INBOUND_DOMAIN}` :
                handle ? `Your address will be ${handle}@${INBOUND_DOMAIN}` : `Your address will be yourhandle@${INBOUND_DOMAIN}`
              }
              slotProps={{
                formHelperText: {
                  sx: {
                    color: handleError || handleStatus === 'taken' ? 'error.main' :
                           handleStatus === 'available' || handleSaved ? 'success.main' : undefined,
                  },
                },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      {handleStatus === 'checking' && <CircularProgress size={16} />}
                      {handleStatus === 'available' && <CheckCircleIcon color="success" fontSize="small" />}
                      {handleStatus === 'taken' && <ErrorIcon color="error" fontSize="small" />}
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                        @{INBOUND_DOMAIN}
                      </Typography>
                    </InputAdornment>
                  ),
                },
              }}
              sx={{ flex: 1 }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleSaveHandle}
              disabled={!handle || handleStatus === 'taken' || handleStatus === 'invalid' || handleStatus === 'checking' || handleSaving}
              sx={{ mt: 0.5, minWidth: 80 }}
            >
              {handleSaving ? <CircularProgress size={16} /> : 'Save'}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Box sx={{ p: 3 }}>
          <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 1 }}>
            Second Inbound Email Address
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            An alternate address for sending or forwarding emails to Stash.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              size="small"
              value={handle2}
              onChange={e => handleHandle2Change(e.target.value)}
              placeholder="yourhandle"
              helperText={
                handle2Error ? handle2Error :
                handle2Status === 'invalid' ? 'Use 3–32 lowercase letters, numbers, dots, hyphens' :
                handle2Status === 'taken' ? 'This mailbox is not available' :
                handle2Status === 'available' ? `Your address: ${handle2}@${INBOUND_DOMAIN}` :
                handle2Saved ? `Saved! Email to ${handle2}@${INBOUND_DOMAIN}` :
                handle2 ? `Your address will be ${handle2}@${INBOUND_DOMAIN}` : `Your address will be yourhandle@${INBOUND_DOMAIN}`
              }
              slotProps={{
                formHelperText: {
                  sx: {
                    color: handle2Error || handle2Status === 'taken' ? 'error.main' :
                           handle2Status === 'available' || handle2Saved ? 'success.main' : undefined,
                  },
                },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      {handle2Status === 'checking' && <CircularProgress size={16} />}
                      {handle2Status === 'available' && <CheckCircleIcon color="success" fontSize="small" />}
                      {handle2Status === 'taken' && <ErrorIcon color="error" fontSize="small" />}
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                        @{INBOUND_DOMAIN}
                      </Typography>
                    </InputAdornment>
                  ),
                },
              }}
              sx={{ flex: 1 }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleSaveHandle2}
              disabled={!handle2 || handle2Status === 'taken' || handle2Status === 'invalid' || handle2Status === 'checking' || handle2Saving}
              sx={{ mt: 0.5, minWidth: 80 }}
            >
              {handle2Saving ? <CircularProgress size={16} /> : 'Save'}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Button
        variant="outlined"
        color="error"
        startIcon={<LogoutIcon />}
        onClick={logout}
        fullWidth
      >
        Sign out
      </Button>
    </Box>
  );
}
